<template lang="pug">
.smart-drive
    managementPage.scrollable(:title="$t(`${baseTranslationPath}.name`)"
                  @fetchData="loadList"
                  @openEditor="openEditor"
                  @addItem="addItem"
                  :searchPageSize="searchPageSize",
                  :showTotals="true"
                  :teamFilter="true"
                  :teamOptions="teamOptions"
                  :pagination="pagination"
                  :currentPath="currentPath"
                  :isSmartDrive="true"
                  :isSharePointDir="isImmutableDir"
                  :isAdvancedCrawlingEnabled="isAdvancedCrawlingEnabled"
                  :creatable="canView('/settings/smartdrive/new')"
                  ref="managementPage")
      router-view(v-slot:editor)
      template(v-slot:body)
        manangeNavigation(:list="directoryList" @clickItem="clickItem")
        aiq-table.manage-documents-scrollbar(:data="items" :default-sort="defaultSortOrder" v-loading="loading")
          aiq-table-column(:label="$t(`${baseTranslationPath}.title`)"
                          label-class-name="is-bordered"
                          min-width="60"
                          :sort-method="onSortTitle"
                          :sortable="true")
                template(v-slot:default="scope")
                   .title(@click="openDocument(scope.row)" :class="{ 'title-hover': isHovered === scope.$index }" @mouseover="isHovered = $event.target.closest('tr').rowIndex" @mouseleave="isHovered = null")
                    SharePointSvg(v-if="getObjectType(scope.row) === 'sharepoint'")
                    SftpIcon(v-else-if="getObjectType(scope.row) === 'sftp'")
                    font-awesome-icon(v-else :icon="getTitle(scope.row)[0]" :class="getTitle(scope.row)[1]")
                    span {{ scope.row.name }}
          aiq-table-column(prop="updated_at"
                          :label="$t(`${baseTranslationPath}.updated`)"
                          label-class-name="is-bordered"
                          min-width="50"
                          :sortable="true"
                          :sort-method="onSortUpdatedAt")
                template(v-slot:default="scope")
                   span {{ getItemUpdatedAt(scope.row) }}
          aiq-table-column(
                          :label="$t(`${baseTranslationPath}.smart_control`)"
                          label-class-name="is-bordered"
                          min-width="50")
                template(v-slot:default="scope")
                  span {{ scope.row.is_smart_control_enabled ? $t('global.yes') : $t('global.no') }}
          aiq-table-column(:label="$t(`${baseTranslationPath}.who_can_access`)"
                          label-class-name="is-bordered"
                          prop="description"
                          min-width="90")
            template(v-slot:header)
              span {{ $t(`${baseTranslationPath}.who_can_access`) }} &nbsp;
              aiq-tooltip(placement="top")
                template(v-slot:content)
                  div {{ $t(`${baseTranslationPath}.column_teams_tooltip`) }}
                i.iq-ico-explanation
            template(v-slot:default="scope")
                aiq-keyword-list(:list="teamList(scope.row)")
          aiq-table-column(:label="$t(`${baseTranslationPath}.segments`)"
                          label-class-name="is-bordered"
                          min-width="90")
            template(v-slot:header)
              span {{ $t(`${baseTranslationPath}.segments`) }} &nbsp;
              aiq-tooltip(placement="top")
                template(v-slot:content)
                  div {{ $t(`${baseTranslationPath}.column_segments_tooltip`) }}
                i.iq-ico-explanation
            template(v-slot:default="scope")
                aiq-keyword-list(:list="segmentList(scope.row)")
          aiq-table-column(v-if="canView('/settings/smartdrive/:number')"
                          align="center"
                          label-class-name="is-bordered"
                          width="71")
            template(v-slot:default="scope")
              aiq-button(v-if="!isReadOnly(scope.row)" link type="primary" size="small", @click="openEditor(scope.row)") {{ $t(`global.manage`) }}
    </template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import get from 'lodash/get';
import set from 'lodash/set';
import dayjs from '@/localPlugins/dayjs';
import cloneDeep from 'lodash/cloneDeep';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import SharePointSvg from '@/assets/svg/sharepoint.vue';
import SftpIcon from '@/assets/svg/sftp-icon.vue';
import { PAGINATION_DEFAULT, TEAM_LIMIT } from '@/constants/pagination';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';
import { iconClasses, RECORD_KINDS } from './smartDriveUtil';
import manangeNavigation from '@/components/manageNavigator.vue';
const SEARCH_PAGE_SIZE = 25;

export default {
  name: 'Smart Drive List',
  mixins: [GetGlobalSource],
  components: {
    ManagementPage,
    SharePointSvg,
    manangeNavigation,
    SftpIcon,
  },
  data() {
    return {
      pagination: PAGINATION_DEFAULT,
      loading: true,
      currentPath: '',
      isHovered: null,
      searchPageSize: SEARCH_PAGE_SIZE,
      baseTranslationPath: 'settings_tab.smart_drive_tab.manage',
      directoryList: [],
      isImmutableDir: false,
      currentDirectory: {
        id: 0,
        name: 'Resources',
        hasNext: false,
      },
      defaultSortOrder: { prop: 'updated_at', order: 'ascending' },
    };
  },
  computed: {
    ...mapState({
      items: (state) => state.documents.documents,
      agentProfile: (state) => state.agent.profile,
      isAdvancedCrawlingEnabled: state => state.featureFlags.FEATURE_FLAGS.ADVANCED_CRAWLING,
    }),
    ...mapGetters({
      sortedTeams: 'teams/sortedTeams',
      segments: 'configs/segments',
    }),
    teamOptions() {
      return this.canView('/users/filters/show_all_teams')
        ? this.sortedTeams
        : this.agentProfile.teams;
    },
  },
  created() {
    this.$_get = (object, key, defaultValue) => get(object, key, defaultValue);
  },
  async mounted() {
    await this.getTeamsList([{ limit: TEAM_LIMIT }]);
    await this.getCustomerSegments();
    /**
     * Using $refs to have access to the variable bound to the teams filter dropdown.
     * Default behavior is selecting all teams to which I have access to.
     */
    set(this.$refs, 'managementPage.teamOption', cloneDeep(this.teamOptions));
  },
  watch: {
    '$route.query': {
      immediate: true,
      async handler(newQuery) {
        const parentId = get(newQuery, 'parent_id', 'home');
        this.currentPath = parentId;
        const isImmutable = [RECORD_KINDS.SHAREPOINT, RECORD_KINDS.SFTP, RECORD_KINDS.HUBSPOT].includes(get(newQuery, 'type'));
        this.isImmutableDir = isImmutable;
        await this.loadList({
          searchTerm: '',
          searchOffset: 0,
          teamOption: this.teamOptions,
        });
      },
    },
  },
  methods: {
    ...mapActions({
      getTeamsList: 'teams/getTeamsList',
      getCustomerSegments: 'configs/getCustomerSegments',
    }),
    teamList(record) {
      const teams = get(record, 'teams', []);
      const names = teams.map((team) => team.name);
      const isGenAi = get(record, 'meta.genai', false);

      if (!isGenAi) {
        return [];
      }

      if (names.length === 0) {
        return [this.$t(`${this.baseTranslationPath}.all`)];
      }

      return [...new Set(names)];
    },
    segmentList(record) {
      const segments = get(record, 'segments', []);
      const isGenAi = get(record, 'meta.genai', false);
      if (!isGenAi) {
        return [];
      }
      const names = segments.map((segment) => segment.name);
      const uniqueNames = [...new Set(names)];
      const totalSegments = get(this, 'segments.length', 0);

      if (uniqueNames.length === totalSegments) {
        return [this.$t(`${this.baseTranslationPath}.all`)];
      }

      return uniqueNames;
    },
    /**
     * Fetches the list of documents based on the provided params.
     *
     * @param {Object} params - The parameters to pass to the documents/getDocumentListV2 action.
     * @param {string} params.searchTerm - The search term to filter the documents by.
     * @param {number} params.searchOffset - The offset of the documents to fetch.
     * @param {array} params.teamOption - The teams to filter the documents by.
     * @param {string} [params.parent_id] - The parent_id of the directory to fetch documents from.
     *
     * @returns {Promise<Object>} - The response from the documents/getDocumentListV2 action.
     */
    async loadList({ searchTerm, searchOffset, teamOption }) {
      const params = {
        limit: this.searchPageSize,
        query: searchTerm,
        offset: searchOffset,
        teams: teamOption.map((x) => x.id),
      };
      const queryParentId = get(this.$route, 'query.parent_id');

      if (!queryParentId) {
        this.directoryList = [{
          id: 0,
          name: 'Resources',
          hasNext: false,
        }];
      }
      if (queryParentId) {
        params.parent_id = queryParentId;
      }
      const response = await this.$store.dispatch(
        'documents/getDocumentListV2',
        [params, searchOffset === 0],
      );
      this.loading = false;
      this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },
    /**
     * Navigates to the folder represented by the given item.
     * If the item has no parent_id, it navigates to the root of the SmartDrive.
     * It also removes all items in the directoryList from the current index to the end
     * and sets the last item in the list to have hasNext equal to false.
     * @param {Object} item - the item to navigate to
     */
    clickItem(item) {
      if (!item.parent_id) {
        this.$router.push({ path: this.$route.path });
      }
      //if last item on the list, do nothing.
      if (!item.hasNext) {
        return;
      }
      const query = {
        parent_id: item.parent_id,
      };
      if (item.isImmutableSubdirectory) {
        query.type = item.isImmutableSubdirectory;
      }
      this.$router.push({ path: this.$route.path, query });

      //get current index of selected item
      const currentIndex = this.directoryList.findIndex(
        (x) => x.id === item.id,
      );

      //remove from next index to end of list
      this.directoryList.splice(
        currentIndex + 1,
        this.directoryList.length - currentIndex,
      );
      if (this.directoryList.length) {
        this.directoryList[this.directoryList.length - 1].hasNext = false;
      }
    },
    /**
     * Navigates to the folder represented by the given record.
     * If the record represents a folder or a sharepoint, it navigates to that folder.
     * If the record represents a URL or HTML, it opens the URL in a new tab.
     * If the record represents any other type, it calls onHandleView and opens the document in the viewer.
     * @param {Object} record - the record to navigate to
     */
    async openDocument(record) {
      const object_type = get(record, 'object_type', '');
      if (
        object_type === RECORD_KINDS.FOLDER ||
        object_type === RECORD_KINDS.SHAREPOINT ||
        object_type === RECORD_KINDS.SFTP ||
        object_type === RECORD_KINDS.URLLIST ||
        object_type === RECORD_KINDS.HUBSPOT
      ) {
        // update the state of the second to the last item on the list with a has next value
        // this mimics a simple linked list
        const penUltimateItem =
          this.directoryList[this.directoryList.length - 1];
        if (penUltimateItem) {
          this.directoryList[this.directoryList.length - 1].hasNext = true;
        }

        //update the current directory
        this.currentDirectory = {
          name: get(record, 'name', ''),
          hasNext: false,
          id: this.directoryList.length,
          parent_id: record.id,
        };

        const isImmutableSub = get(this.$route, 'query.type');
        //if in sub directory, push the current directory
        //else push the home directory
        if (isImmutableSub) {
          this.currentDirectory.isImmutableSubdirectory = isImmutableSub;
        }
        this.directoryList.push(this.currentDirectory);


        if (
          object_type === RECORD_KINDS.SHAREPOINT ||
          object_type === RECORD_KINDS.SFTP ||
          object_type === RECORD_KINDS.HUBSPOT) {
          this.$router.push({
            path: this.$route.path,
            query: { parent_id: record.id, type: object_type },
          });
        } else {
          const parentId = record.id;
          const isImmutableSubdirectory = isImmutableSub;
          const query = isImmutableSubdirectory
            ? { parent_id: parentId, type: isImmutableSubdirectory }
            : { parent_id: parentId };
          this.$router.push({ path: this.$route.path, query });
        }
        return;
      }
      if (
        object_type === RECORD_KINDS.URL ||
        object_type === RECORD_KINDS.HTML
      ) {
        const link = get(record, 'file.payload.url');
        if (link) {
          window.open(link, '_blank');
        } else {
          window.open(get(record, 'meta.url'), '_blank');
        }
        return;
      }

      await this.onHandleView(record);
      return;
    },
    isReadOnly(record) {
      return get(record, 'meta.readonly', false);
    },
    /**
   * Opens the editor for the given record.
   * If the record represents a file, it opens the file editor.
   * If the record represents a URL or HTML, it opens the URL editor.
   * If the record represents a folder, it opens the folder editor.
   * If the record represents a sharepoint, it opens the sharepoint editor.
   * @param {Object} record - the record to open in the editor
   * @param {string} type - the type of the editor to open (default is 'url')
   */
    openEditor(record) {
      this.record = {
        ...this.$store.getters['documents/documentModel'],
        ...record,
      };

      const recordId = this.record.id || 'new';
      const path = `${this.$route.path}/${recordId}`;
      const queryParentId = get(this.$route, 'query.parent_id');

      const kind = get(this.record, 'object_type', '');
      let type = RECORD_KINDS.FILE;

      switch (kind) {
        case RECORD_KINDS.FOLDER:
          type = 'folder';
          break;
        case RECORD_KINDS.URL:
        case RECORD_KINDS.HTML:
        case '':
          type = 'url';
          break;
        case RECORD_KINDS.SFTP:
          type = 'sftp';
          break;
        case RECORD_KINDS.SHAREPOINT:
          type = 'sharepoint';
          break;
        case RECORD_KINDS.URLLIST:
          type = 'urllist';
          break;
        case RECORD_KINDS.HUBSPOT:
          type = 'hubspot';
          break;
        default:
          break;
      }

      if (queryParentId && recordId === 'new') {
        const path = `${this.$route.path}/${queryParentId}`;
        this.$router.push({ path, query: { type, appendItem: true } });
      } else if (!queryParentId && recordId === 'new') {
        this.$router.push({ path, query: { type: 'url' } });
      } else if (queryParentId && recordId !== 'new') {
        this.$router.push({ path, query: { type, hidePermission: true } });
      }
      else {
        this.$router.push({ path, query: { type } });
      }
    },
    addItem(type) {
      const queryParentId = get(this.$route, 'query.parent_id');
      if (queryParentId) {
        const path = `${this.$route.path}/${queryParentId}`;
        this.$router.push({ path, query: { type, appendItem: true } });
      } else {
        const path = `${this.$route.path}/new`;
        this.$router.push({ path, query: { type } });
      }
    },

    getItemUpdatedAt(item) {
      const updated_time = get(item, 'updated_at', '');
      return dayjs(updated_time).format('MMM D, YYYY h:mm A');
    },

    getObjectType(item) {
      return get(item, 'object_type', '');
    },
    getTitle(item) {
      const object_type = get(item, 'object_type', '');
      return iconClasses(object_type);
    },

    onSortTitle(a, b) {
      const aValue = a.name.toLowerCase().replace(/\s+/g, '');
      const bValue = b.name.toLowerCase().replace(/\s+/g, '');
      return aValue < bValue ? 1 : -1;
    },
    onSortUpdatedAt(a, b) {
      const aValue = a.updated_at;
      const bValue = b.updated_at;
      return aValue < bValue ? 1 : -1;
    },

    async onHandleView(document) {
      const url = await this.getFileUrl(get(document, 'file.payload'));
      if (url) {
        window.open(url, '_blank');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "./icon.scss";

.smart-drive {
  height: calc(100vh - 150px);
  overflow-y: auto;
}
:deep(.keyword-list .el-tag--keyword) {
  background-color: #59b4ff !important;
}
:deep(.el-checkbox__input.is-disabled.is-checked .el-checkbox__inner) {
  background-color: #59b4ff !important;
  border-color: #59b4ff !important;
}
:deep(tr) {
  height: 50px !important;
}
:deep(.content-inner) {
  width: 99% !important;
}

:deep(.el-select__tags-text) {
    display: inline-grid;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 32px;
  }

.checkbox {
  padding-top: 3px;
}

.title-hover {
  color: #4671B3;
  text-decoration: underline;
  font-weight: bold;
}

.title {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-size: 13px;
}

</style>
